import type { ReactNode } from "react"
import { Trans, useLingui } from "@lingui/react/macro"
import { msg } from "@lingui/core/macro"
import type { MessageDescriptor } from "@lingui/core"
import { CheckIcon, SparkleIcon } from "./icons"

// Small visual pill variants reused across the Paper Companies
// designs. Each variant maps to a specific design treatment so
// callers don't have to redeclare bg/border colors.

interface IndustryPillProps {
  label: string
}

// Quiet beige pill for the industry column on the index table.
export function IndustryPill({ label }: IndustryPillProps) {
  return (
    <span className="inline-flex items-center bg-[#F0EDE6] rounded text-[11px] leading-[14px] text-[#5C544A] px-2 py-0.5">
      {label}
    </span>
  )
}

interface NeedsEnrichmentBadgeProps {
  compact?: boolean
}

// Inline orange "Needs enrichment" badge shown next to the
// company subtitle when the AI hasn't backfilled industry / size
// yet. The mobile variant drops the rounded background to fit
// the smaller row.
export function NeedsEnrichmentBadge({
  compact = false,
}: NeedsEnrichmentBadgeProps) {
  if (compact) {
    return (
      <span className="inline-flex items-center gap-1 text-[12px] leading-4 text-[#B86A2D]">
        <SparkleIcon size={9} color="#B86A2D" />
        <Trans>Needs enrichment</Trans>
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-[3px] bg-[#FAF3EB] rounded px-[5px] py-px text-[10px] font-semibold tracking-[0.04em] uppercase leading-3 text-[#B86A2D]">
      <SparkleIcon size={9} color="#B86A2D" />
      <Trans>Needs enrichment</Trans>
    </span>
  )
}

interface LifecyclePillProps {
  label: string
}

// "Customer" green dot pill shown next to the company name on
// the show page hero.
export function LifecyclePill({ label }: LifecyclePillProps) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] leading-4 text-[#5C8A4F]">
      <span
        aria-hidden="true"
        className="size-1.5 rounded-full bg-[#5C8A4F]"
      />
      {label}
    </span>
  )
}

type DealStatusPillKind = "needs_you" | "ai_working" | "won" | "lost"

interface DealStatusPillProps {
  kind: DealStatusPillKind
  // Fixed-width slot keeps the pill aligned in a vertical lane
  // across stacked rows. Drop it for mobile where the pill
  // sits above the row title instead.
  fixedWidth?: boolean
}

const DEAL_STATUS_TEXT: Record<DealStatusPillKind, MessageDescriptor> = {
  needs_you: msg`Needs you`,
  ai_working: msg`AI working`,
  won: msg`Won`,
  lost: msg`Lost`,
}

// Status pill on each Deals row in the Company show page. Color
// scheme matches the Paper "Coca-Cola" deal mood pills.
export function DealStatusPill({
  kind,
  fixedWidth = true,
}: DealStatusPillProps) {
  const { i18n } = useLingui()
  const widthClass = fixedWidth ? "w-[100px]" : ""
  const text = i18n._(DEAL_STATUS_TEXT[kind])
  const base =
    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] leading-3 shrink-0"

  if (kind === "needs_you") {
    return (
      <span
        className={`${base} ${widthClass} bg-[#FBF1F0] border border-[#E5BFBA] text-[#B6675F]`}
      >
        <SparkleIcon size={9} color="#B6675F" />
        {text}
      </span>
    )
  }

  if (kind === "ai_working") {
    return (
      <span
        className={`${base} ${widthClass} bg-[#FAF3EB] border border-[#E8D6BD] text-[#B86A2D]`}
      >
        <SparkleIcon size={9} color="#B86A2D" />
        {text}
      </span>
    )
  }

  if (kind === "won") {
    return (
      <span
        className={`${base} ${widthClass} bg-[#EAF2E5] text-[#5C8A4F]`}
      >
        <CheckIcon size={9} color="#5C8A4F" />
        {text}
      </span>
    )
  }

  // lost
  return (
    <span
      className={`${base} ${widthClass} bg-[#F0EDE6] text-[#5C544A]`}
    >
      {text}
    </span>
  )
}

interface TagPillProps {
  label: ReactNode
  dashed?: boolean
}

// Right-rail tag pill on the show page. Dashed variant is the
// "+ Add tag" affordance.
export function TagPill({ label, dashed = false }: TagPillProps) {
  const borderClass = dashed
    ? "border-dashed border-[#C9C2B6] text-[#7A746B]"
    : "border-solid border-[#E5DFD3] text-[#5C544A]"

  return (
    <span
      className={`inline-flex items-center bg-card rounded-full border px-2.5 py-1 text-[11px] leading-[14px] ${borderClass}`}
    >
      {label}
    </span>
  )
}
